/**
 * GameTitle renders the current game name in the header.
 *
 * Why: The game title has custom font styling and a conditional glow effect.
 * Extracting it keeps GameControls focused on layout.
 *
 * When to use: Rendered by GameControls when a game is active.
 */

export interface GameTitleProps {
  /** The name of the current game */
  gameName: string
  /** Accent color for the title */
  accentColor?: string
  /** Custom font family for the title */
  titleFont?: string
  /** Whether dark mode is active (affects glow effect) */
  isDarkMode?: boolean
}

export function GameTitle({
  gameName,
  accentColor,
  titleFont,
  isDarkMode,
}: GameTitleProps) {
  return (
    <div className="min-w-0 max-w-[200px] shrink">
      <h2
        className="font-semibold truncate whitespace-nowrap leading-none m-0"
        title={gameName}
        data-testid="header-game-name"
        style={{
          ...(accentColor ? {
            color: accentColor,
            fontWeight: 'bold',
            // Only apply text shadow in dark mode to avoid background-like appearance in light mode
            ...(isDarkMode ? { textShadow: `0 0 8px ${accentColor}40` } : {})
          } : {}),
          ...(titleFont ? { fontFamily: `'${titleFont}', sans-serif` } : {}),
          fontSize: 'calc(var(--menu-bar-height,48px) * 0.42)',
          lineHeight: '1',
        }}
      >
        {gameName}
      </h2>
    </div>
  )
}
